import React, { useRef, useState, useEffect } from "react";
import { Eraser } from "lucide-react";

/**
 * Draw-your-signature canvas (Legistify-style). Pure HTML5 — no deps.
 * Supports mouse + touch + pen. Exports to PNG data-URL.
 *
 * Props:
 *   onChange(dataUrl): called whenever the user stops drawing. If the canvas is
 *     empty, dataUrl is an empty string.
 *   height (default 140): canvas height in px. Width is 100%.
 */
const SignatureCanvas = ({ onChange, height = 140 }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const hasInkRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1E1B4B";
    ctx.lineWidth = 2.2;
    // White bg so exported PNG is readable
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    resize();
    const obs = new ResizeObserver(resize);
    if (canvasRef.current) obs.observe(canvasRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPosRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPosRef.current = p;
    hasInkRef.current = true;
    if (isEmpty) setIsEmpty(false);
  };

  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasInkRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onChange && onChange(dataUrl);
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setIsEmpty(true);
    onChange && onChange("");
  };

  return (
    <div className="w-full" data-testid="signature-canvas-wrapper">
      <div className="relative border-2 border-dashed border-gray-300 rounded-xl bg-white">
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, touchAction: "none", cursor: "crosshair" }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={stop}
          data-testid="signature-canvas"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-300 text-sm italic">Draw your signature here</p>
          </div>
        )}
      </div>
      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={clear}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#E83E8C]"
          data-testid="signature-clear-btn"
        >
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
};

export default SignatureCanvas;
